import {
  LogLevel,
  ComponentCode,
  stepTypeFamily,
  getPromptInputMeta,
  QUESTION_TYPES,
  OUTPUT_SCHEMA_TYPES,
  PROMPT_OUTPUT_SCHEMA,
  ALL_SECTION_NAMES,
} from '@padcanvas/shared';
import type {
  TracingBackend,
  WebTracingBackend,
  TracingProviderConfig,
  TracingSettings,
  WebTracingSettings,
  LLMProviderFamily,
  PromptType,
  StepType,
  OutputSchemaType,
  StepRunConfig,
  PipelineStepConfig,
  PipelineStrategyConfig,
  OperationConfig,
  OperationsMap,
  RateLimitTier,
  RateLimitConfig,
  AppSettings,
  PromptInputMeta,
  EligibilityDefinition,
  ProviderConfig,
  ProviderConfigDoc,
  PipelineConfigDoc,
  SubscriptionDefinition,
  UserSubscription,
  Tier,
  ProviderTieredSet,
  TierSlot,
  ProviderParallelSet,
  ParallelSelection,
  ConfigBundle,
  ConfigBundleSections,
  ConfigSectionName,
  ConfigDiffItem,
  ConfigDiffResult,
  ConfigImportDryRunResult,
  ConfigImportRequest,
  ConfigImportApplyResult,
  ConfigApplyResult,
  Targeting,
} from '@padcanvas/shared';

// Re-export from shared for convenience
export { LogLevel, ComponentCode, stepTypeFamily, getPromptInputMeta, OUTPUT_SCHEMA_TYPES, PROMPT_OUTPUT_SCHEMA, ALL_SECTION_NAMES };
export type { StepFamily } from '@padcanvas/shared';
export { targetingMatches } from '@padcanvas/shared';
export type { Targeting, TargetingContext, ProficiencyLevel } from '@padcanvas/shared';
export type { TracingBackend, WebTracingBackend, TracingProviderConfig, TracingSettings, WebTracingSettings, LLMProviderFamily, PromptType, StepType, OutputSchemaType, StepRunConfig, PipelineStepConfig, PipelineStrategyConfig, OperationConfig, OperationsMap, RateLimitTier, RateLimitConfig, AppSettings, PromptInputMeta, EligibilityDefinition, ProviderConfig, ProviderConfigDoc, PipelineConfigDoc, SubscriptionDefinition, UserSubscription, Tier, ProviderTieredSet, TierSlot, ProviderParallelSet, ParallelSelection, ConfigBundle, ConfigBundleSections, ConfigSectionName, ConfigDiffItem, ConfigDiffResult, ConfigImportDryRunResult, ConfigImportRequest, ConfigImportApplyResult, ConfigApplyResult };

export interface ProviderAvailability {
  // Dynamic — keys come from CosmosDB provider config (no longer hardcoded)
  [key: string]: boolean;
}


// Re-export domain option lists
export { DIFFICULTY_OPTIONS, PROFICIENCY_OPTIONS } from '@shared-ui/question-set-types';
export { SUBJECT_OPTIONS, GRADE_OPTIONS, PROGRAMME_OPTIONS, REGISTRABLE_PROGRAMME_OPTIONS } from '@padcanvas/shared';
export const QUESTION_TYPE_OPTIONS = QUESTION_TYPES;

export interface PromptInfo {
  id: string;
  name: string;
  description: string;
  version: number;  // Simple integer version: 1, 2, 3...
  /** User prompt template (renamed from `template` in Phase 1 of the rename). */
  userPrompt?: string;
  /** Optional system prompt template — sent to the LLM as `instructions`/`system`. */
  systemPrompt?: string;
  targeting?: Targeting;
  type: PromptType;
  assessmentField?: string;
  assessmentSourceType?: string;
  /** Optional JSON Schema for strict structured output. When set, providers
   *  that support schema enforcement will constrain the model output. */
  jsonSchema?: { name: string; schema: object };
  /** Per-prompt strict-JSON-schema toggle. `undefined` = inherit capability default. */
  useStrictJsonSchema?: boolean;
  /** Identifies the LLM output shape this prompt asks for. Drives scorer
   *  dispatch — see `docs/FeatureDesigns/AssessmentAndMarking/ScorerDispatch.md`. Optional in the
   *  type only because legacy prompts predate the field; new prompts should
   *  always set it. */
  outputSchemaType?: OutputSchemaType;
  /** ISO timestamp of the last save. Optional — pre-Phase-1 prompts may
   *  not carry it. The admin Prompts list shows it as the "Updated" column. */
  updatedAt?: string;
  /** Provenance fields — populated by Phase 4+ when reads go through the
   *  prompt-manifest. See docs/FeatureDesigns/PipelineInfra/prompt-versioning.md. */
  source?: 'git' | 'admin';
  gitSha?: string;
  activatedAt?: string;
  activatedBy?: string;
  overrideReason?: string;
}

export interface PromptVersionInfo {
  id: string;
  version: number;
  name: string;
  description: string;
  /** User prompt template (renamed from `template` in Phase 1 of the rename). */
  userPrompt: string;
  systemPrompt?: string;
  targeting?: Targeting;
  assessmentField?: string;
  assessmentSourceType?: string;
  jsonSchema?: { name: string; schema: object };
  useStrictJsonSchema?: boolean;
  outputSchemaType?: OutputSchemaType;
  updatedAt: string;
  archivedAt?: string;
  /** Provenance — only set on new version docs (Phase 3+). Legacy archives
   *  predate the source field and leave this undefined. */
  source?: 'git' | 'admin';
  gitSha?: string;
}

// Placeholder arrays are now generated from the shared registry:
// import { getPromptInputMeta } from '@padcanvas/shared';
// Use getPromptInputMeta(type) to get PromptInputMeta[] for any prompt type.

